/**
 *
 *  Homepage UK
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 24/05/2024.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Transition, {
  ENTERING,
  ENTERED,
  EXITING,
  EXITED,
} from "react-transition-group/Transition";
import Cookies from "js-cookie";
import Modal from "react-modal";
import Slider from "react-slick";
import { Title, Meta } from "react-meta-elements";
import Lottie from "react-lottie";

import { useAppState } from "../../../state";

// Components
import ClampLines from "../../ClampLines/ClampLines";
import Header from "../../Homepage/Header/Header";
import Footer from "../../Footer/Footer";
import Button from "../../Button/Button";
import ScrollToTopOnMount from "../../ScrollToTopOnMount/ScrollToTopOnMount";
import ContentScroller from "../../ContentScroller/ContentScroller";
import SpecialisationItem from "../../Homepage/SpecialisationItem/SpecialisationItem";
import AskConciergeModal from "../AskConciergeModal/AskConciergeModal";
import PrevButton from "../../TruBeDateTimePicker/PrevButton/PrevButton";
import NextButton from "../../TruBeDateTimePicker/NextButton/NextButton";
import PartnersScroller from "../../Homepage/PartnersScroller/PartnersScroller";
import TrainerCard from "../../Homepage/PartnersScroller/TrainerCard/TrainerCard";
import SliderPrevArrow from "../../SliderPrevButton/SliderPrevButton";
import SliderNextArrow from "../../SliderNextButton/SliderNextButton";
import Testimonials from "../../Testimonials/Testimonials";
import GymsSlider from "../../GymsSlider/GymsSlider";
import { RegionSelectModal } from "../../RegionSelectModal/RegionSelectModal";

// Hooks
import useForwardRef from "../../../hooks/useForwardRef/useForwardRef";
import useTheme from "../../ThemeProvider/useTheme/useTheme";
import useContentScrollerControls from "../../../hooks/useContentScrollerControls/useContentScrollerControls";
import { useEvent } from "../../../hooks/useEvent/useEvent";
import useDeviceCapabilities from "../../../hooks/useDeviceCapabilities/useDeviceCapabilities";
import useButtonsHidden from "../../TruBeDateTimePicker/useButtonsHidden/useButtonsHidden";
import useDebouncedCallback from "../../../hooks/useDebouncedCallback/useDebouncedCallback";

// Icons
import ArrowLeftIcon from "../../../icons/ArrowLeftIcon";
import ArrowRightIcon from "../../../icons/ArrowRightIcon";
import CloseIcon from "../../../icons/CloseIcon";
import BlockerIcon from "../../../icons/BlockerIcon";
import CheckNiceIcon from "../../../icons/CheckNiceIcon";
import CheckNiceOIcon from "../../../icons/CheckNiceOIcon";

// Utils
import { clientWidth } from "../../../utils";
import { modalStyles } from "../../../constants";
import { getScrollTarget } from "../../../utils/getScrollTarget";

// Images
import Lotus from "../../Homepage/Lotus";
import AppleAppStore from "../../Homepage/AppleAppStore";
import HeartIcon from "../../Homepage/HeartIcon";
import HighQualityIcon from "../../Homepage/HighQualityIcon";
import LocationIcon from "../../Homepage/LocationIcon";

import OneOnOneSessionsIcon from "../../Homepage/OneOnOneSessionsIcon";
import CaretDownIcon from "../../../icons/CaretDownIcon";
import HowItWorks1 from "../../Homepage/HowItWorks1";
import HowItWorks2 from "../../Homepage/HowItWorks2";
import HowItWorks3 from "../../Homepage/HowItWorks3";
import HowItWorks4 from "../../Homepage/HowItWorks4";
import CaretUpIcon from "../../../icons/CaretUpIcon";

import heroImg from "./hero.jpg";
import heroImg2x from "./hero@2x.jpg";
import heroMobileImg from "./hero-mobile.jpg";
import heroMobileImg2x from "./hero-mobile@2x.jpg";
import heroMobileImg3x from "./hero-mobile@3x.jpg";

import ourCoaches from "./homepage-our-coaches.jpg";
import ourCoaches2x from "./homepage-our-coaches@2x.jpg";
import ourCoaches3x from "./homepage-our-coaches@3x.jpg";
import ourCoachesMobile from "./homepage-our-coaches-mobile.jpg";
import ourCoachesMobile2x from "./homepage-our-coaches-mobile@2x.jpg";
// import ourCoachesMobile3x from "./homepage-our-coaches-mobile@3x.jpg";

import trubeIsLifestyle from "./trube-is-lifestyle.jpg";
import trubeIsLifestyle2x from "./trube-is-lifestyle@2x.jpg";
import trubeIsLifestyle3x from "./trube-is-lifestyle@2x.jpg";
import trubeIsLifestyleMobile from "./trube-is-lifestyle-mobile.jpg";
import trubeIsLifestyleMobile2x from "./trube-is-lifestyle-mobile@2x.jpg";

import trubeIsLifestyleMobile3x from "../../Homepage/trube-is-lifestyle-mobile@3x.jpg";

import disciplineCardPt from "../../Homepage/discipline-card-pt.jpg";
import disciplineCardPt2x from "../../Homepage/discipline-card-pt@2x.jpg";
import disciplineCardPt3x from "../../Homepage/discipline-card-pt@3x.jpg";
import disciplineCardBoxing from "../../Homepage/discipline-card-boxing.jpg";
import disciplineCardBoxing2x from "../../Homepage/discipline-card-boxing@2x.jpg";
import disciplineCardBoxing3x from "../../Homepage/discipline-card-boxing@3x.jpg";
import disciplineCardYoga from "../../Homepage/discipline-card-yoga.jpg";
import disciplineCardYoga2x from "../../Homepage/discipline-card-yoga@2x.jpg";
import disciplineCardYoga3x from "../../Homepage/discipline-card-yoga@3x.jpg";
import disciplineCardPilates from "../../Homepage/discipline-card-pilates.jpg";
import disciplineCardPilates2x from "../../Homepage/discipline-card-pilates@2x.jpg";
import disciplineCardPilates3x from "../../Homepage/discipline-card-pilates@3x.jpg";
import disciplineCardPrePostNatal from "../../Homepage/discipline-card-prepost-natal.jpg";
import disciplineCardPrePostNatal2x from "../../Homepage/discipline-card-prepost-natal@2x.jpg";
import disciplineCardPrePostNatal3x from "../../Homepage/discipline-card-prepost-natal@3x.jpg";
import disciplineCardStretching from "../../Homepage/discipline-card-stretching.jpg";
import disciplineCardStretching2x from "../../Homepage/discipline-card-stretching@2x.jpg";
import disciplineCardStretching3x from "../../Homepage/discipline-card-stretching@3x.jpg";
import disciplineCardRunning from "../../Homepage/discipline-card-running.jpg";
import disciplineCardRunning2x from "../../Homepage/discipline-card-running@2x.jpg";

import disciplineCardRunning3x from "../../Homepage/discipline-card-running@3x.jpg";

import iphoneSliderLp from "./iphone-slider-location-picker.jpg";
import iphoneSliderLp2x from "./iphone-slider-location-picker@2x.jpg";
import iphoneSliderLp3x from "./iphone-slider-location-picker@3x.jpg";

import iphoneSliderPersonal from "../../../images/iphone-slider-personal.png";

import askConcierge from "../../Homepage/AskConciergeModal/ask-concierge.svg";
import accessImg from "../../Homepage/access.jpg";
import access2xImg from "../../Homepage/access@2x.jpg";

import access3xImg from "../../Homepage/access@3x.jpg";
import connectImg from "../../Homepage/connect.jpg";
import connect2xImg from "../../Homepage/connect@2x.jpg";

import connect3xImg from "../../Homepage/connect@3x.jpg";
import trainImg from "../../Homepage/train.jpg";
import train2xImg from "../../Homepage/train@2x.jpg";

import train3xImg from "../../Homepage/train@3x.jpg";
import progressImg from "../../Homepage/progress.jpg";
import progress2xImg from "../../Homepage/progress@2x.jpg";
import progress3xImg from "../../Homepage/progress@3x.jpg";

import animationData from "../../../lotties/scroll-down";
import animationDataMobile from "../../../lotties/hand-scroll";
import TruBe from "../../Homepage/TruBe";
import lifestyleCollectiveLogo from "../../Homepage/lifestyle-collective.png";
import lifestyleCollectiveLogo2x from "../../Homepage/lifestyle-collective@2x.png";
import lifestyleCollectiveLogo3x from "../../Homepage/lifestyle-collective@3x.png";
import ScrollingIndicator from "../../Homepage/ScrollingIndicator/ScrollingIndicator";
import MovingBar from "../../MovingBar/MovingBar";
import lifestyle1 from "../LifestyleLp/gallery-1@2x.jpg";
import lifestyle2 from "../LifestyleLp/gallery-2@2x.jpg";
import lifestyle22 from "../LifestyleLp/gallery-2-2@2x.jpg";
import lifestyle3 from "../LifestyleLp/gallery-3@2x.jpg";
import lifestyle4 from "../LifestyleLp/gallery-4@2x.jpg";
import lifestyle5 from "../LifestyleLp/gallery-5@2x.jpg";
import lifestyle6 from "../LifestyleLp/gallery-6@2x.jpg";
import lifestyle7 from "../LifestyleLp/gallery-7@2x.jpg";
import HeadlineWithSeparator from "../../HeadlineWithSeparator/HeadlineWithSeparator";
import UpcomingExperiencesSlider from "../../LifestyleLP/UpcomingExperiencesSlider/UpcomingExperiencesSlider";
import { EventCard } from "../../LifestyleLP/LifestyleLP";
import LogoBar from "../../LogoBar/LogoBar";
import Carousel from "react-gallery-carousel";
import CorporateContactForm from "../../CorporateContactForm/CorporateContactForm";
import logoBandier from "../../Homepage/logo-bandier.png";
import logoBandier2x from "../../Homepage/logo-bandier@2x.png";
import logoBandier3x from "../../Homepage/logo-bandier@3x.png";
import logoPendry from "../../Homepage/logo-pendry.png";
import logoPendry2x from "../../Homepage/logo-pendry@2x.png";
import logoPendry3x from "../../Homepage/logo-pendry@3x.png";
import logoCymbiotika from "../../Homepage/logo-cymbiotika.png";
import logoCymbiotika2x from "../../Homepage/logo-cymbiotika@2x.png";
import logoCymbiotika3x from "../../Homepage/logo-cymbiotika@3x.png";
import logoNeueHouse from "../../Homepage/logo-neuehouse.png";
import logoNeueHouse2x from "../../Homepage/logo-neuehouse@2x.png";
import logoNeueHouse3x from "../../Homepage/logo-neuehouse@3x.png";
import logoOnRun from "../../Homepage/logo-on-run.png";
import logoOnRun2x from "../../Homepage/logo-on-run@2x.png";
import logoOnRun3x from "../../Homepage/logo-on-run@3x.png";
import logoSoho from "../../Homepage/logo-soho.png";
import logoSoho2x from "../../Homepage/logo-soho@2x.png";
import logoSoho3x from "../../Homepage/logo-soho@3x.png";
import logoSpringPlace from "../../Homepage/logo-spring-place.png";
import logoSpringPlace2x from "../../Homepage/logo-spring-place@2x.png";
import logoSpringPlace3x from "../../Homepage/logo-spring-place@3x.png";
import logoWme from "../../Homepage/logo-wme.png";
import logoWme2x from "../../Homepage/logo-wme@2x.png";
import logoWme3x from "../../Homepage/logo-wme@3x.png";
import solutionsHotels from "../CorporatePage/corporate-page-solutions-hotels.png";
import solutionsHotels2x from "../CorporatePage/corporate-page-solutions-hotels@2x.png";
import solutionsHospitality from "../CorporatePage/corporate-page-solutions-hospitality.png";
import solutionsHospitality2x from "../CorporatePage/corporate-page-solutions-hospitality@2x.png";
import solutionsCoWorkingSpaces from "../CorporatePage/corporate-page-solutions-co-working-spaces.png";
import solutionsCoWorkingSpaces2x from "../CorporatePage/corporate-page-solutions-co-working-spaces@2x.png";
import solutionsCareHomes from "../CorporatePage/corporate-page-solutions-care-homes.png";
import solutionsCareHomes2x from "../CorporatePage/corporate-page-solutions-care-homes@2x.png";
import solutionsEvents from "../CorporatePage/corporate-page-solutions-events.png";
import solutionsEvents2x from "../CorporatePage/corporate-page-solutions-events@2x.png";
import AppBanner from "../AppBanner/AppBanner";

const galleryImages = [
  lifestyle1,
  lifestyle2,
  lifestyle22,
  lifestyle3,
  lifestyle4,
  lifestyle5,
  lifestyle6,
  lifestyle7,
];

const sliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: { slidesToShow: 1, centerMode: true, centerPadding: "32px" },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 2,
      centerMode: true,
      centerPadding: "50px",
      initialSlide: 1,
    },
  },
  {
    breakpoint: 1180,
    settings: {
      slidesToShow: 3,
      centerMode: true,
      centerPadding: "100px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 3,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 2,
    },
  },
  {
    breakpoint: 2560,
    settings: {
      slidesToShow: 5,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 5,
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 5,
      slidesToScroll: 2,
      centerMode: true,
      centerPadding: "167px",
      initialSlide: 5,
    },
  },
  { breakpoint: 10000, settings: "unslick" },
];

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: "xMidYMid slice",
  },
};

const SCROLL_DOWN_TRESHOLD = 150;

const AccordionItem = ({ className, title, text }) => {
  const [collapsed, setCollapsed] = useState(true);

  const handleClick = useCallback(() => {
    setCollapsed(!collapsed);
  }, [collapsed]);

  return (
    <div className="tbk-mb-2 tbk-border-b tbk-border-solid tbk-border-blue-grey-light">
      <button
        className={`${
          className ? `${className} ` : ""
        }tbk-flex tbk-w-full tbk-items-start tbk-justify-between tbk-pb-1 tbk-text-primary hover:tbk-text-coral`}
        onClick={handleClick}
      >
        <div className="tbk-text-h3-subtitle tbk-grow tbk-text-left">
          {title}
        </div>
        {collapsed ? <CaretDownIcon size={32} /> : <CaretUpIcon />}
      </button>
      <div
        className="tbk-text-main tbk-overflow-hidden tbk-text-left tbk-text-primary tbk-transition-height"
        style={{
          height: collapsed ? "0" : "auto",
          marginBottom: collapsed ? "0" : "24px",
        }}
      >
        {text}
      </div>
    </div>
  );
};

export default function Homepage() {
  const location = useLocation();

  const { theme } = useTheme();

  const {
    user,
    hasTriedToAutoLogin,
    bookingsWithoutRating,
    specialisations,
    referralCode,
    partnersBySpecialisation,
    gyms: gymsData,
    startedEvents,
    upcomingEvents,
    completedEvents,
    loadBookingsWithoutRating,
    applyReferral,
    setPromoCode,
    setReferralCode,
    loadSpecialisations,
    loadAvailablePartnersBySpecialisation,
    loadGyms,
    loadUpcomingEvents,
    loadCompletedEvents,
    clearUpcomingEventsLoaded,
    clearCompletedEventsLoaded,
  } = useAppState();

  const [activeSlide, setActiveSlide] = useState(1);
  const [slideDirection, setSlideDirection] = useState("left");
  const [readMore, setReadMore] = useState(false);
  const [showMoreQuestions, setShowMoreQuestions] = useState(false);
  const [isAskConciergeModalOpen, setIsAskConciergeModalOpen] = useState(false);
  const [isGalleryCarouselModalOpen, setIsGalleryCarouselModalOpen] =
    useState(null);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [currentSpecialisation, setCurrentSpecialisation] =
    useState("Endurance");
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [storedPageYOffset, setStoredPageYOffset] = useState();
  const [_lastKnownPageYOffset, setLastKnownPageYOffset] = useState();
  const [galleryInitialSlide, setGalleryInitialSlide] = useState(0);

  const heroRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);
  const getAvailablePartnersCancelablePromiseRef = useRef();
  const lastKnownPageYOffsetRef = useRef();
  const questionsSectionRef = useRef();

  const { supportsTouchCapableExperience } = useDeviceCapabilities(),
    // slotsCount = monthChunks.reduce((acc, item) => {
    //   acc += item.length;
    //
    //   return acc;
    // }, 0),
    [_supportsTouchCapableExperience, setSupportsTouchCapableExperience] =
      useState(supportsTouchCapableExperience ? 1 : 0);

  const slickInitialised = true;

  useEffect(() => {
    setSupportsTouchCapableExperience(supportsTouchCapableExperience ? 1 : 0);
  }, [supportsTouchCapableExperience]);

  const contentScrollerRef = useRef(null),
    [_contentScrollerRef, setContentScrollerRef] =
      useForwardRef(contentScrollerRef);

  const firstElementRef = useRef(null);
  const showScrollIndicatorRef = useRef();

  const recalcReset = useEvent(() => {
    _contentScrollerRef &&
      _contentScrollerRef.recalc({
        reason: "reset",
      });
  });

  useEffect(() => {
    _contentScrollerRef && recalcReset();
  }, [recalcReset, _contentScrollerRef]);

  useEffect(() => {
    setTimeout(() => {
      showScrollIndicatorRef.current = true;
      setShowScrollIndicator(true);
    }, 3000);
  }, [recalcReset, _contentScrollerRef]);

  const handleScrollUp = useCallback(() => {}, []);

  const handleScrollDown = useCallback(() => {
    const windowPageYOffset = window.pageYOffset;

    if (
      windowPageYOffset > SCROLL_DOWN_TRESHOLD &&
      showScrollIndicatorRef.current === true
    ) {
      showScrollIndicatorRef.current = false;
      setShowScrollIndicator(false);
    }

    setStoredPageYOffset(windowPageYOffset);
  }, []);

  const handleScroll = useCallback(
    () => {
      const lastKnownPageYOffset = lastKnownPageYOffsetRef.current;
      const windowPageYOffset = window.pageYOffset;

      // Fixes iOS bug when Header cannot "dock"
      if (lastKnownPageYOffset === windowPageYOffset) {
        return;
      }

      if (lastKnownPageYOffset > windowPageYOffset) {
        handleScrollUp();
      } else {
        handleScrollDown();
      }

      setLastKnownPageYOffset(windowPageYOffset);
      lastKnownPageYOffsetRef.current = windowPageYOffset;
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleScrollDebounced = useDebouncedCallback(handleScroll, 300);

  useEffect(
    () => {
      window.addEventListener("scroll", handleScrollDebounced);

      // Not needed
      //loadGyms({ withLogos: true });

      return () => {
        window.removeEventListener("scroll", handleScrollDebounced);
      };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (showScrollIndicatorRef.current === false) {
      window.removeEventListener("scroll", handleScrollDebounced);
    }
  }, [showScrollIndicator]);

  const handleScrollIndicatorClick = React.useCallback(() => {
    if (showScrollIndicatorRef.current) {
      firstElementRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const recalcChildrenChangedEvent = useEvent(() => {
    _contentScrollerRef &&
      setTimeout(() =>
        _contentScrollerRef.recalc({
          reason: "children_changed",
        })
      );
  });

  useEffect(() => {
    if (specialisations && specialisations.length && _contentScrollerRef) {
      _contentScrollerRef.recalc({
        reason: "reset",
      });
      // setTimeout(() =>
      //   _contentScrollerRef.recalc({
      //     reason: "children_changed",
      //   })
      // );
    }
  }, [specialisations, _contentScrollerRef]);

  useEffect(() => {
    _contentScrollerRef &&
      _supportsTouchCapableExperience > 0 &&
      recalcChildrenChangedEvent();
  }, [
    recalcChildrenChangedEvent,
    _contentScrollerRef,
    _supportsTouchCapableExperience,
  ]);

  const isLoading = false;

  const [hiddenPrev, hiddenNext] = useButtonsHidden({
      contentScrollerApi: _contentScrollerRef,
      itemCount: 16, //specialisations.length,
      isLoading: isLoading,
    }),
    { onPressForward, onPressReverse } = useContentScrollerControls({
      contentScrollerApi: _contentScrollerRef,
      loop: false,
      unit: "slide",
      getScrollTarget,
    });

  // useEffect(() => {
  //   _contentScrollerRef &&
  //   (onScrollerMounted == null || onScrollerMounted(_contentScrollerRef));
  // }, [onScrollerMounted, _contentScrollerRef]);

  const isUserLoggedIn = !!(user && user.id);

  const loadPartnersBySpecialisation = useCallback((specialisation, user) => {
    getAvailablePartnersCancelablePromiseRef.current &&
      getAvailablePartnersCancelablePromiseRef.current.cancel();

    var cancelablePromise = loadAvailablePartnersBySpecialisation(
      specialisation.value,
      user ? user.region : "GB" /*process.env.REACT_APP_REGION*/
    );

    getAvailablePartnersCancelablePromiseRef.current = {
      ...cancelablePromise,
      promise: cancelablePromise.promise.then((res) => {
        // if (!res.data.length) {
        //   getAvailablePartnersCancelablePromiseRef.current =
        //     loadAvailablePartnersBySpecialisation(
        //       "TEST_GB",
        //       user ? user.region : process.env.REACT_APP_REGION
        //     ); // Endurance by default
        // }
      }),
    };
  }, []);

  React.useEffect(() => {
    if (!hasTriedToAutoLogin) {
      return;
    }
    if (user) {
      loadBookingsWithoutRating(user.id, user.region);
    }

    loadSpecialisations(
      user ? user.region : "GB" /*process.env.REACT_APP_REGION*/
    ).then((res) => {
      loadPartnersBySpecialisation(
        res.data && res.data.length ? res.data[0] : { value: "TEST_GB" },
        user // ? user.region : process.env.REACT_APP_REGION
      ); // Will load coaches by "ENDURANCE" specialisation
    });
    // loadGyms({
    //   withLogos: true,
    //   region: user ? user.region : process.env.REACT_APP_REGION,
    // });

    // loadCompletedEvents(
    //   user && user.id,
    //   "COMPLETED",
    //   { sort: "start,desc", page: 0, size: 4 },
    //   user ? user.region : undefined//"GB"
    // );

    loadUpcomingEvents(
      user && user.id,
      "UPCOMING,STARTED,UPCOMING_FULL",
      { sort: "start", page: 0, size: 8 },
      user ? user.region : "GB"
    );
  }, [user, hasTriedToAutoLogin]);

  React.useEffect(() => {
    var query = new URLSearchParams(location.search);
    var promoCode = query.get("promocode");
    var referralCode = query.get("referral");

    if (referralCode) {
      Cookies.set("referral", referralCode, { path: "/" });
    } else {
      // Remember referral code to survive page reloads (for Apple Login for example)
      referralCode = Cookies.get("referral");
    }

    if (promoCode) {
      Cookies.set("promocode", promoCode, { path: "/" });
    } else {
      // For Apple Sign-in
      promoCode = Cookies.get("promocode");
    }

    if (promoCode) {
      setPromoCode(promoCode);
    }

    if (referralCode) {
      setReferralCode(referralCode);
    }
  }, [location]);

  const openGalleryCarouselModal = (index) => {
    setIsGalleryCarouselModalOpen(true);
    setGalleryInitialSlide(index);
  };

  const closeGalleryCarouselModal = () => {
    setIsGalleryCarouselModalOpen(false);
  };

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  React.useEffect(() => {
    if (user && referralCode) {
      applyReferral(user.id, referralCode)
        .then((res) => {
          // Referral code is valid
          openInfoModal(
            `You received ${res.data.symbol}${res.data.receiverReward} referral credits.`,
            "Referral Bonus Applied"
          );
        })
        .catch((res) => {
          openInfoModal(res.message, "Link Error");
        })
        .finally(() => {
          // Regardless the referral code is valid or not we don't need it anymore
          // (so it won't be applied on re-login for example)
          setReferralCode("");

          Cookies.remove("referral");
        });
    }
  }, [user, referralCode]);

  const handleMoveSliderLeft = () => {
    setSlideDirection("left");

    if (activeSlide === 3) {
      setActiveSlide(2);
    } else if (activeSlide === 2) {
      setActiveSlide(1);
    }
  };

  const handleMoveSliderRight = () => {
    setSlideDirection("right");

    if (activeSlide === 1) {
      setActiveSlide(2);
    } else if (activeSlide === 2) {
      setActiveSlide(3);
    }
  };

  const handleMoreQuestionsClick = useCallback(() => {
    setShowMoreQuestions(true);
  }, []);

  const handleReadMoreClick = useCallback(() => {
    setReadMore(true);
  }, []);

  const handleSpecialisationClick = useCallback(
    (specialisation) => {
      loadPartnersBySpecialisation(specialisation, user);

      setCurrentSpecialisation(specialisation.name);
    },
    [user]
  );

  const handleSpecialisationVisibleChange = useCallback(
    (key, inView, element) => {
      // console.log("onSpecialisationVisibleChange", key, inView, element);
    },
    []
  );

  const handleRegionClick = useCallback(() => {
    var date = new Date();

    // Remember for 30 days
    date.setDate(date.getDate() + 30);

    Cookies.set("region-selected", "GB" /*process.env.REACT_APP_REGION*/, {
      path: "/",
      expires: date,
    });
  }, []);

  const futureEvents =
    startedEvents && startedEvents.length > 0
      ? [].concat(startedEvents, upcomingEvents)
      : upcomingEvents;

  return (
    <>
      <ScrollToTopOnMount />
      <Title>TruBe: Top Fitness & Wellbeing Classes & Personal Trainers</Title>
      <Meta
        name="description"
        content="TruBe offers the best fitness & wellbeing group classes and one-to-one trainers for Pilates, Yoga, Personal Training, HIIT, Running, and more. Tailored to you!"
      />
      <Header
        showRegionSelect
        isLoggedIn={isUserLoggedIn}
        heroRef={heroRef}
        region={"GB"}
        returnTo="/"
        onFaqClick={() =>
          questionsSectionRef.current.scrollIntoView({ behavior: "smooth" })
        }
        onChooseRegionClick={handleRegionClick}
      />

      <div
        ref={heroRef}
        className="tbk-relative tbk-flex tbk-h-[690px] tbk-items-end tbk-bg-[#3B5057] tbk-pt-7 md:tbk-items-center lg:tbk-h-[80vh] lg:tbk-min-h-[664px]"
      >
        <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-top-0 tbk-left-0 tbk-h-85 tbk-w-full tbk-overflow-hidden md:tbk-h-full lg:tbk-min-h-[664px]">
          {/*<video*/}
          {/*  id="1861853d-95a4-61c7-d0d8-195590981e6c-video"*/}
          {/*  autoPlay={true}*/}
          {/*  loop={true}*/}
          {/*  className="tbk-absolute tbk-m-auto tbk-h-full tbk-w-full tbk-bg-cover tbk-bg-center tbk-object-cover"*/}
          {/*  style={{*/}
          {/*    backgroundImage:*/}
          {/*      clientWidth() > 960*/}
          {/*        ? "url(https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4503c5e474856cedf7fc_Vid_Muted-poster-00001.jpg)"*/}
          {/*        : "url(https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4546a7eac6a0eb5d23a5_mobile-background-poster-00001.jpg)",*/}
          {/*  }}*/}
          {/*  muted={true}*/}
          {/*  playsInline={true}*/}
          {/*>*/}
          {/*  {clientWidth() > 960 ? (*/}
          {/*    <>*/}
          {/*      <source src="https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4503c5e474856cedf7fc_Vid_Muted-transcode.mp4" />*/}
          {/*      <source src="https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4503c5e474856cedf7fc_Vid_Muted-transcode.webm" />*/}
          {/*    </>*/}
          {/*  ) : (*/}
          {/*    <>*/}
          {/*      <source src="https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4546a7eac6a0eb5d23a5_mobile-background-transcode.mp4" />*/}
          {/*      <source src="https://uploads-ssl.webflow.com/60785b43631c8625d8c10c7e/650c4546a7eac6a0eb5d23a5_mobile-background-transcode.webm" />*/}
          {/*    </>*/}
          {/*  )}*/}
          {/*</video>*/}
          {/*<div className="tbk-absolute tbk-h-full tbk-w-full tbk-bg-gradient-to-r tbk-from-basic-trueblack/60 tbk-to-basic-trueblack/60" />*/}
          <picture className="tbk-h-full tbk-grow">
            <source
              media="(min-width: 768px)"
              srcSet={`${heroImg} 1x, ${heroImg2x} 2x`}
            />
            <source srcSet={`${heroMobileImg} 1x, ${heroMobileImg2x} 2x`} />

            <img
              className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"
              src={heroImg}
              alt="Hero"
            />
          </picture>
        </div>
        <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">
          <div className="tbk-mt-2_ lg:tbk-mt-7_ tbk-h-full_ tbk-mb-2_ tbk-flex tbk-grow tbk-flex-col tbk-items-center tbk-justify-end tbk-pb-8 md:tbk-justify-start lg:tbk-pt-4">
            <div className="tbk-mb-2 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center md:tbk-justify-start">
              <TruBe width={105} height={32} className="tbk-mb-1" />
              <img
                className="md:tbk-h-24"
                src={lifestyleCollectiveLogo}
                srcSet={`${lifestyleCollectiveLogo} 1x, ${lifestyleCollectiveLogo2x} 2x, ${lifestyleCollectiveLogo3x} 3x`}
                alt="Lifestyle Collective"
              />
            </div>

            <div className="tbk-overflow-hidden tbk-pt-2 tbk-pb-0 tbk-will-change-background md:tbk-w-100 md:tbk-rounded-2xl md:tbk-px-3 md:tbk-pt-3 md:tbk-pb-3 md:tbk-backdrop-blur-xl">
              {/*<div className="tbk-mb-3 tbk-flex tbk-flex-col tbk-items-center tbk-justify-center md:tbk-justify-center">*/}
              {/*</div>*/}

              <p className="tbk-text-main tbk-mb-2 tbk-text-center tbk-text-basic-white">
                Join the TruBe Lifestyle Collective for curated group fitness &
                wellness experiences led by top coaches. Access up to three
                events monthly for £54/month. Immerse yourself in a supportive
                environment, connect with like-minded individuals, and
                prioritize your well-being
              </p>
              <div className="tbk-text-center md:tbk-text-left">
                <Link to={"/lifestyle"}>
                  <Button
                    size="md"
                    variant="secondary"
                    className="tbk-mt-1 tbk-inline-flex tbk-w-full"
                    endIcon={<ArrowRightIcon />}
                  >
                    Learn More
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/*<div className="tbk-relative tbk-h-full tbk-pb-8 tbk-pt-2 md:tbk-grid md:tbk-grid-cols-2 md:tbk-items-end lg:tbk-grid-cols-homepage-hero lg:tbk-pb-8 lg:tbk-pt-6">*/}
          {/*  <div className="">*/}
          {/*    <div className="tbk-text-epic-bold lg:tbk-text-extra-bold tbk-mb-4 tbk-text-basic-white lg:tbk-whitespace-nowrap">*/}
          {/*      Discover*/}
          {/*      <br /> Your*/}
          {/*      <br />*/}
          {/*      TruBest*/}
          {/*    </div>*/}
          {/*    <p className="tbk-text-h3-subtitle lg:tbk-text-title-medium tbk-mb-4 tbk-text-basic-white">*/}
          {/*      In-person expert trainers and wellness professionals in the*/}
          {/*      comfort of your own home, at your convenience.*/}
          {/*    </p>*/}
          {/*    <Link*/}
          {/*      to={isUserLoggedIn || true ? "/partners" : "/become-a-member"}*/}
          {/*    >*/}
          {/*      <Button*/}
          {/*        variant={"secondary"}*/}
          {/*        className="tbk-mb-4 tbk-inline-block tbk-w-68 lg:tbk-mb-0"*/}
          {/*        endIcon={<ArrowRightIcon />}*/}
          {/*      >*/}
          {/*        {isUserLoggedIn || true ? "View Trainers" : "Join Now"}*/}
          {/*      </Button>*/}
          {/*    </Link>*/}
          {/*  </div>*/}
          {/*  <div className="tbk-flex md:tbk-justify-end">*/}
          {/*    <a*/}
          {/*      href="https://apps.apple.com/us/app/trube/id6449003944"*/}
          {/*      className=""*/}
          {/*    >*/}
          {/*      <AppleAppStore className="" width={165} height={48} />*/}
          {/*    </a>*/}
          {/*  </div>*/}
          {/*</div>*/}

          <ScrollingIndicator
            className={
              "tbk-absolute tbk-right-2 tbk-bottom-1 md:tbk-absolute md:tbk-bottom-5 md:tbk-right-auto md:tbk-left-1/2 md:tbk--translate-x-1/2"
            }
            onClick={handleScrollIndicatorClick}
          />
        </div>
      </div>

      <div
        ref={firstElementRef}
        className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-9 lg:tbk-pb-9"
      >
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <div className="lg:tbk-mb-7 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="">
              <h2 className="tbk-text-title-bold lg:tbk-text-h2-black lg:!tbk-leading-none_ tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-2">
                Connect, Inspire, Flourish: <br className="lg:tbk-hidden" />
                Join TruBe's Wellness <br className="lg:tbk-hidden" />
                Community
              </h2>
              <p className="tbk-text-main tbk-mb-[48px] tbk-text-primary lg:tbk-mb-0">
                Step into TruBe’s lifestyle collective and gain access to
                fitness and well-being classes that go beyond the mundane
                workout. Join our community and discover weekly classes led by
                the best coaches of their chosen disciplines, including HIIT,
                Boxing, Pilates, Yoga, Meditation, Sound Healing, and more.
                <br />
                <br />
                Held at exclusive venues, each class provides additional service
                offerings from our brand partners, whilst immersing you in a
                supportive environment, connecting you with like-minded
                individuals, and forging meaningful relationships as you improve
                your well-being.
              </p>
              {/*<Link to={isUserLoggedIn ? "/partners" : "/become-a-member"}>*/}
              {/*  <Button*/}
              {/*    size="md"*/}
              {/*    className="tbk-mx-auto tbk-w-64 lg:tbk-mx-0"*/}
              {/*    endIcon={<ArrowRightIcon />}*/}
              {/*  >*/}
              {/*    {isUserLoggedIn ? "Start Now" : "Become a Member"}*/}
              {/*  </Button>*/}
              {/*</Link>*/}
            </div>
            <div>
              <div className="lg:tbk-mb-4_ tbk-grid tbk-grid-cols-2 tbk-justify-between tbk-gap-2 lg:tbk-justify-start">
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary">
                  <HeartIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Gifts & Discounts from
                    <br />
                    Fitness Brands
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary">
                  <HighQualityIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Curated by Certified Specialists
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pb-3 tbk-pt-2 tbk-text-center tbk-text-primary xl:tbk-block ">
                  <LocationIcon
                    size={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Immersive Classes at Exclusive Venues
                  </div>
                </div>
                <div className="tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-px-2 tbk-pt-2 tbk-pb-3 tbk-text-center tbk-text-primary">
                  <OneOnOneSessionsIcon
                    width={clientWidth() < 960 ? 54 : null}
                    height={clientWidth() < 960 ? 40 : null}
                    className="tbk-mb-1 tbk-inline-block"
                  />
                  <div className="tbk-text-small lg:tbk-text-h3-subtitle !tbk-font-semibold md:tbk-px-1 lg:!tbk-font-medium">
                    Variety
                    <br />
                    of Disciplines
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="user-select-none tb-moving-bar tbk-flex tbk-w-full tbk-overflow-hidden">
        <MovingBar
          images={galleryImages.map((v) => ({ url: v }))}
          imgClassName="tbk-h-76 tbk-w-76 tbk-rounded-2xl lg:tbk-h-100 lg:tbk-w-100"
          onClick={openGalleryCarouselModal}
        />
        <MovingBar
          images={galleryImages.map((v) => ({ url: v }))}
          imgClassName="tbk-h-76 tbk-w-76 tbk-rounded-2xl lg:tbk-h-100 lg:tbk-w-100"
          onClick={openGalleryCarouselModal}
        />
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-4 tbk-pb-4 lg:tbk-pb-7 lg:tbk-pt-7">
        {/*<div className="tbk-container tbk-px-3 lg:tbk-px-8">*/}
        {/*  */}
        {/*</div>*/}
        <div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">
          <Link
            to={
              isUserLoggedIn
                ? "/lifestyle"
                : {
                    pathname: `/become-a-member`,
                    search: `?return_to=${encodeURIComponent(
                      "/checkout?membershipId=1"
                    )}`,
                    state: { from: "/lifestyle" },
                  }
            }
            className="tbk-inline-block"
          >
            <Button
              //size={"md"}
              className="tbk-w-68"
              endIcon={<ArrowRightIcon />}
              onClick={clearUpcomingEventsLoaded}
            >
              {isUserLoggedIn ? "Learn More" : "Become a Member"}
            </Button>
          </Link>
        </div>
      </div>

      {futureEvents.length ? (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-4 lg:tbk-pb-5 lg:tbk-pt-9">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8">
            <HeadlineWithSeparator
              component="h3"
              titleClass="tbk-bg-basic-white tbk-text-blue-grey tbk-text-h3-subtitle lg:tbk-text-title-bold"
              className="tbk-mb-3 lg:tbk-mb-5"
              separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
            >
              Upcoming Classes
            </HeadlineWithSeparator>
            <div className="tbk-mb-6 ">
              <UpcomingExperiencesSlider
                showArrows={clientWidth() > theme.responsive.breakpoints.lg}
                className="tbk-pb-3"
                itemComponent={EventCard}
                itemProps={{
                  to: !isUserLoggedIn &&
                    false && {
                      pathname: `/become-a-member`,
                      search: "?return_to=/checkout?membershipId=1",
                      state: { from: location.pathname },
                    },
                  imgClassName: "tbk-h-48 lg:tbk-h-76",
                  showRsvpCta: true,
                  isUserLoggedIn: isUserLoggedIn,
                  onRsvpCtaClick: clearUpcomingEventsLoaded,
                }}
                futureEvents={futureEvents}
              />
            </div>
          </div>
          {/*<div className="tbk-mb-3_ lg:tbk-mb-5_ tbk-relative tbk-text-center">*/}
          {/*  <Link to={`/events/upcoming`} className="tbk-inline-block">*/}
          {/*    <Button*/}
          {/*      size={"md"}*/}
          {/*      className="tbk-w-64"*/}
          {/*      endIcon={<ArrowRightIcon />}*/}
          {/*      onClick={clearUpcomingEventsLoaded}*/}
          {/*    >*/}
          {/*      View All Upcoming*/}
          {/*    </Button>*/}
          {/*  </Link>*/}
          {/*</div>*/}
        </div>
      ) : null}

      <div className="tbk-pt-7 tbk-pb-7 lg:tbk-pt-7 lg:tbk-pb-7">
        <LogoBar />
        <div className="tbk-container tbk-mx-auto tbk-px-2 tbk-text-center lg:tbk-px-8">
          <Link to={"/events"} className="tbk-inline-block">
            <Button
              size="md"
              className="tbk-mt-3 tbk-w-64"
              endIcon={<ArrowRightIcon />}
            >
              Reserve Your Spot
            </Button>
          </Link>
        </div>
      </div>

      <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-pt-7 tbk-pb-7 lg:tbk-pt-7 lg:tbk-pb-9">
        <div className="tbk-container tbk-px-2 lg:tbk-px-8">
          <HeadlineWithSeparator
            component="h3"
            textAlign="left"
            titleClass="tbk-bg-basic-white tbk-text-epic-medium lg:tbk-text-epic-bold tbk-text-blue-grey !tbk-leading-none lg:!tbk-leading-tight"
            className="tbk-mb-4 lg:tbk-mb-5"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Private
            <br />
            1:1 Coaching
          </HeadlineWithSeparator>

          <div className="lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-8">
            <div className="tbk-mb-4 lg:tbk-mb-0">
              <h2 className="tbk-text-title-bold lg:tbk-text-h2-black lg:!tbk-leading-none_ tbk-mb-2 !tbk-leading-tight tbk-text-primary lg:tbk-mb-4">
                Achieve Your Potential with{" "}
                <span className="tbk-text-coral">Private Classes</span>,
                Specialist Advice, and Tailored Nutrition Guidance
              </h2>

              <Link className="tbk-hidden lg:tbk-block" to={"/partners"}>
                <Button
                  size="md"
                  className="tbk-mx-auto tbk-w-70 lg:tbk-mx-0"
                  endIcon={<ArrowRightIcon />}
                >
                  Find a Coach Near You
                </Button>
              </Link>
            </div>
            <div>
              <p className="tbk-text-main tbk-mb-4 tbk-text-primary lg:tbk-mb-0">
                At TruBe, we empower individuals to achieve their fitness and
                well-being goals by connecting them with the best coaches. By
                providing a personalized, convenient, and effective 1:1 class,
                we enable you to build and maintain a healthier lifestyle for
                the future.
                <br />
                <br />
                Instilled from our London roots, our concierge styled wellness
                approach treats every customer differently. Whether your goal is
                weight loss, build muscle, or improve your strength and
                conditioning – Our coaches will take the time to contact you,
                establish your individual needs, and curate a program to help
                you achieve your goals.
              </p>
              <Link className="lg:tbk-hidden" to={"/partners"}>
                <Button
                  size="md"
                  className="tbk-mx-auto tbk-w-70 lg:tbk-mx-0"
                  endIcon={<ArrowRightIcon />}
                >
                  Find a Coach Near You
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/*<div className="lg:tbk-h-116_ tbk-relative tbk-flex tbk-flex-col tbk-overflow-hidden tbk-pb-7 md:tbk-bg-home-cymbiotika-banner-gradient md:tbk-pt-4 md:tbk-pb-4 lg:tbk-flex-row">*/}
      {/*  <div className="tbk-right-0 tbk-bottom-0 tbk-top-0 tbk-left-0 tbk--z-1 tbk-flex tbk-w-full tbk-items-end tbk-overflow-hidden md:tbk-absolute">*/}
      {/*    <picture className="tbk-h-full tbk-grow">*/}
      {/*      <source*/}
      {/*        media="(min-width: 768px)"*/}
      {/*        srcSet={`${homeLifestyleCollectiveaBanner} 1x, ${homeLifestyleCollectiveaBanner2x} 2x`}*/}
      {/*      />*/}
      {/*      <source*/}
      {/*        srcSet={`${homeLifestyleCollectiveaBannerMobile} 1x, ${homeLifestyleCollectiveaBannerMobile2x} 2x, ${homeLifestyleCollectiveaBannerMobile3x} 3x`}*/}
      {/*      />*/}
      {/*      <img*/}
      {/*        className="tbk-h-full tbk-w-full tbk-object-cover md:tbk-h-full"*/}
      {/*        src={homeLifestyleCollectiveaBanner}*/}
      {/*        alt="Lifestyle Collective"*/}
      {/*      />*/}
      {/*    </picture>*/}
      {/*  </div>*/}
      {/*  <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-px-2 lg:tbk-px-8">*/}
      {/*    <div className="tbk-relative tbk-h-full md:tbk-grid md:tbk-grid-cols-lifestyle-cols-layout">*/}
      {/*      <div className="tbk-overflow-hidden tbk-rounded-2xl tbk-pt-2 tbk-pb-4 tbk-backdrop-blur-xl tbk-will-change-background md:tbk-px-3 lg:tbk-pt-3 lg:tbk-pb-3">*/}
      {/*        <div className="tbk-mb-3 tbk-hidden tbk-flex-col tbk-items-center tbk-justify-center md:tbk-flex md:tbk-justify-start">*/}
      {/*          <TruBe className="tbk-mb-1" />*/}
      {/*          <img*/}
      {/*            className=""*/}
      {/*            src={lifestyleCollectiveLogo}*/}
      {/*            srcSet={`${lifestyleCollectiveLogo} 1x, ${lifestyleCollectiveLogo2x} 2x, ${lifestyleCollectiveLogo3x} 3x`}*/}
      {/*            alt="Lifestyle Collective"*/}
      {/*          />*/}
      {/*        </div>*/}
      {/*        /!*<h1 className="tbk-text-h2-black md:tbk-text-epic-medium tbk-mb-2 tbk-text-center !tbk-leading-none tbk-text-primary md:tbk-text-left md:tbk-text-basic-white lg:tbk-mb-4 lg:tbk-mb-4">*!/*/}
      {/*        /!*  Cymbiotika Certified*!/*/}
      {/*        /!*</h1>*!/*/}
      {/*        <p className="tbk-text-main tbk-mb-2 tbk-text-primary md:tbk-text-basic-white">*/}
      {/*          Join the TruBe Lifestyle Collective for curated group fitness &*/}
      {/*          wellness experiences led by top coaches. Access up to four*/}
      {/*          events monthly for $34.99/month. Immerse yourself in a*/}
      {/*          supportive environment, connect with like-minded individuals,*/}
      {/*          and prioritize your well-being*/}
      {/*        </p>*/}
      {/*        <div className="tbk-text-center md:tbk-text-left">*/}
      {/*          <Link to={"/lifestyle"}>*/}
      {/*            <Button*/}
      {/*              size="md"*/}
      {/*              // variant="secondary"*/}
      {/*              className="tbk-inline-flex tbk-w-full"*/}
      {/*              endIcon={<ArrowRightIcon />}*/}
      {/*            >*/}
      {/*              Access Now*/}
      {/*            </Button>*/}
      {/*          </Link>*/}
      {/*        </div>*/}
      {/*      </div>*/}
      {/*      <div />*/}
      {/*    </div>*/}
      {/*  </div>*/}
      {/*</div>*/}

      <Slider
        className="home-disciplines-carousel md:tbk-mt-9 lg:tbk-mb-7"
        dots={false}
        infinite={true}
        initialSlide={1}
        speed={500}
        slidesToShow={1}
        slidesToScroll={1}
        nextArrow={
          clientWidth() < 960 ? (
            <SliderNextArrow />
          ) : (
            <SliderNextArrow buttonClassName="tbk-h-9 tbk-w-9" />
          )
        }
        prevArrow={
          clientWidth() < 960 ? (
            <SliderPrevArrow />
          ) : (
            <SliderPrevArrow buttonClassName="tbk-h-9 tbk-w-9" />
          )
        }
        responsive={sliderResponsiveSettings}
      >
        <div id="slide-1" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content slide-bg js-progressive-image tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-bg-home-discipline-card-gradient tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardBoxing}
                srcSet={`${disciplineCardBoxing2x} 2x, ${disciplineCardBoxing3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/kickboxing?session_type=Kickboxing">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Kickboxing
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Control & Power
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-2" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{
              width: "100%",
              height: "100%",
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardStretching}
                srcSet={`${disciplineCardStretching2x} 2x, ${disciplineCardStretching3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/stretching?session_type=Stretching">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Stretching
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Fluidity & Movement
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-3" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardPt}
                srcSet={`${disciplineCardPt2x} 2x, ${disciplineCardPt3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/personal-training?session_type=Personal+Training">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Personal Training
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Functionality & Body Awareness
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-4" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-3 tbk-pt-3 tbk-pb-3 tbk-shadow-trube tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardYoga}
                srcSet={`${disciplineCardYoga2x} 2x, ${disciplineCardYoga3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/yoga?session_type=Yoga">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg ">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Yoga
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Balance & Mindfulness
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-5" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardPilates}
                srcSet={`${disciplineCardPilates2x} 2x, ${disciplineCardPilates3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/pilates?session_type=Pilates">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Pilates
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Confidence & Longevity
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-6" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardPrePostNatal}
                srcSet={`${disciplineCardPrePostNatal2x} 2x, ${disciplineCardPrePostNatal3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/prepost-natal?session_type=Pre%2FPost+Natal">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Pre/Post Natal
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Perseverance & Uplifting
                </div>
              </div>
            </Link>
          </div>
        </div>

        <div id="slide-7" className="slide tbk-h-100 tbk-px-1">
          <div
            className="content js-progressive-image slide-bg tbk-relative tbk-w-full tbk-overflow-hidden tbk-rounded-lg tbk-px-2 tbk-pt-3 tbk-pb-2 tbk-grayscale tbk-transition-all tbk-duration-300 hover:tbk-grayscale-0"
            style={{ width: "100%", height: "100%" }}
          >
            <div className="home-slider-img-wrapper tbk-absolute tbk-left-0 tbk-top-0 tbk-h-full tbk-w-full">
              <img
                className="tbk-h-full tbk-w-full tbk-object-cover "
                src={disciplineCardRunning}
                srcSet={`${disciplineCardRunning2x} 2x, ${disciplineCardRunning3x} 3x`}
                alt="Personal Trainers in Los Angeles"
                aria-hidden="true"
              />
            </div>
            <Link to="/partners/running?session_type=Running">
              <div className="tbk-relative tbk-z-1 tbk-flex tbk-h-full tbk-flex-col tbk-justify-end tbk-rounded-lg">
                <div className="tbk-text-epic-medium tbk-mb-1 !tbk-leading-none tbk-text-basic-white">
                  Running
                </div>
                <div className="tbk-text-secondary tbk-text-basic-white">
                  Perseverance & Uplifting
                </div>
              </div>
            </Link>
          </div>
        </div>
      </Slider>

      <div className="tbk-relative tbk-bg-basic-white tbk-pt-4 tbk-pb-7 lg:tbk-pt-0 lg:tbk-pb-0">
        <div className="tbk-container tbk-z-1 tbk-mx-auto lg:tbk-px-0 lg:tbk-pb-9">
          <hr className="tbk-mx-2 tbk-border-blue-grey-light" />
          <div
            className={`specialisations${hiddenPrev ? " hidden-prev" : ""}${
              hiddenNext ? " hidden-next" : ""
            }`}
          >
            <ContentScroller
              autoRecalcOnMount={false}
              controls={
                !isLoading && (
                  <PrevButton
                    onPress={onPressReverse}
                    className="md:tbk-bottom-6 md:tbk-left-0"
                    buttonClassName="tbk-mb-1.5"
                    visible={
                      !hiddenPrev &&
                      specialisations &&
                      specialisations.length > 1
                    }
                  />
                )
              }
              trailingControls={
                !isLoading && (
                  <NextButton
                    onPress={onPressForward}
                    className="md:tbk-bottom-6 md:tbk-right-0"
                    buttonClassName="tbk-mb-1.5"
                    visible={
                      !hiddenNext &&
                      specialisations &&
                      specialisations.length > 1
                    }
                  />
                )
              }
              description=""
              ref={setContentScrollerRef}
              ariaScrollerRole="radiogroup"
              style={{
                position: "relative",
              }}
            >
              {specialisations.map((specialisation, index) => (
                <SpecialisationItem
                  key={specialisation.name}
                  itemKey={index}
                  active={specialisation.name === currentSpecialisation}
                  specialisationInfo={specialisation}
                  contentScrollerApi={_contentScrollerRef}
                  onSpecialisationVisibleChange={
                    handleSpecialisationVisibleChange
                  }
                  onSpecialisationClick={handleSpecialisationClick}
                />
              ))}
            </ContentScroller>
          </div>
          <div className="tbk-h-68_">
            <PartnersScroller
              partners={partnersBySpecialisation}
              renderPartner={({
                info: partnerInfo,
                index,
                contentScrollerApi,
                onCardVisibleChange,
              }) => (
                <Link to={`/partners/${partnerInfo.id}`}>
                  <TrainerCard
                    trainer={partnerInfo}
                    key={partnerInfo.id}
                    itemKey={index}
                    contentScrollerApi={contentScrollerApi}
                    onCardVisibleChange={onCardVisibleChange}
                    // onCardClick={handleCardClick}
                  />
                </Link>
              )}
            />
          </div>
        </div>
      </div>

      <section
        id="get-in-touch"
        //ref={getInTouchRef}
        className="cta-form-section tbk-mb-4_ tbk-relative tbk-bg-basic-white tbk-pt-7 tbk-pb-7 md:tbk-pt-7 md:tbk-pb-7"
      >
        <div className="tbk-container tbk-mx-auto tbk-px-2 lg:tbk-px-7">
          <HeadlineWithSeparator
            component="h3"
            textAlign="left"
            titleClass="tbk-bg-basic-white tbk-text-epic-medium lg:tbk-text-epic-bold tbk-text-blue-grey !tbk-leading-none lg:!tbk-leading-tight"
            className="tbk-mb-4 lg:tbk-mb-6"
            separatorClass={"tbk--mt-1.5 lg:tbk--mt-2"}
          >
            Corporate
            <br />
            Experiences
          </HeadlineWithSeparator>

          <div className="tbk-mb-[48px] tbk-flex tbk-flex-col tbk-items-start tbk-justify-center lg:tbk-grid-cols-2 lg:tbk-flex-row lg:tbk-items-start lg:tbk-gap-8">
            <div className="tbk--order-1_ md:tbk-w-112_ lg:tbk-order-1_ tbk-mb-4 tbk-w-full lg:tbk-mb-0">
              <p className="tbk-text-main tbk-mb-3">
                TruBe offers tailored group fitness classes for businesses of
                all sizes, designed to enhance employee well-being, boost
                productivity, and foster team cohesion. With years of experience
                in delivering personalized fitness solutions, our team brings
                energy and expertise to each session, ensuring that participants
                of all fitness levels can engage and benefit. These classes not
                only promote physical health but also reduce stress and improve
                mental clarity, making them a valuable investment in a
                healthier, more motivated workforce.
              </p>
              {/*<Link to={"/cymbiotika"}>*/}
              <Button
                size="md"
                variant="secondary"
                className="tbk-w-52_ tbk-inline-flex"
                endIcon={<ArrowRightIcon />}
                onClick={() => setIsAskConciergeModalOpen(true)}
              >
                Learn More about Corporate Experience
              </Button>
              {/*</Link>*/}
            </div>
            <div className="md:tbk-w-112_ tbk-w-full">
              <CorporateContactForm />
            </div>
          </div>

          <div className="tbk-mt-1 tbk-mb-6 tbk-flex tbk-flex-wrap tbk-justify-evenly tbk-gap-3 lg:tbk-justify-between">
            <img
              className="tbk-shrink-0"
              src={logoBandier}
              srcSet={`${logoBandier} 1x, ${logoBandier2x} 2x, ${logoBandier3x} 3x`}
              alt="Bandier"
            />

            <img
              className="tbk-shrink-0"
              src={logoPendry}
              srcSet={`${logoPendry} 1x, ${logoPendry2x} 2x, ${logoPendry3x} 3x`}
              alt="Pendry"
            />

            <img
              className="tbk-shrink-0"
              src={logoCymbiotika}
              srcSet={`${logoCymbiotika} 1x, ${logoCymbiotika2x} 2x, ${logoCymbiotika3x} 3x`}
              alt="Cymbiotika"
            />

            <img
              className="tbk-shrink-0"
              src={logoNeueHouse}
              srcSet={`${logoNeueHouse} 1x, ${logoNeueHouse2x} 2x, ${logoNeueHouse3x} 3x`}
              alt="NeueHouse"
            />

            <img
              className="tbk-shrink-0"
              src={logoOnRun}
              srcSet={`${logoOnRun} 1x, ${logoOnRun2x} 2x, ${logoOnRun3x} 3x`}
              alt="On Run"
            />

            <img
              className="tbk-shrink-0"
              src={logoSoho}
              srcSet={`${logoSoho} 1x, ${logoSoho2x} 2x, ${logoSoho3x} 3x`}
              alt="SOHO"
            />

            <img
              className="tbk-shrink-0"
              src={logoSpringPlace}
              srcSet={`${logoSpringPlace} 1x, ${logoSpringPlace2x} 2x, ${logoSpringPlace3x} 3x`}
              alt="Spring Place"
            />

            <img
              className=""
              src={logoWme}
              srcSet={`${logoWme} 1x, ${logoWme2x} 2x, ${logoWme3x} 3x`}
              alt="WME"
            />
          </div>

          <div className="tbk-mb-4 tbk-items-center md:tbk-mb-0">
            <div className="tbk-flex tbk-grid-cols-2 tbk-flex-wrap tbk-gap-2 lg:tbk-grid lg:tbk-grid-cols-corporate-solutions">
              {/* First */}
              <div className="tbk-relative tbk-grow tbk-basis-2/5 tbk-overflow-hidden lg:tbk-basis-auto">
                <picture className="js-progressive-image tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-lg ">
                  <source
                    srcSet={`${solutionsHotels} 1x, ${solutionsHotels2x} 2x`}
                    media="(minWidth: 760px)"
                    type="image/png"
                  />
                  <source
                    srcSet={`${solutionsHotels} 1x, ${solutionsHotels2x} 2x`}
                    type="image/png"
                  />
                  <img
                    className="tbk-inline-block tbk-h-full tbk-w-full tbk-rounded-lg tbk-object-cover"
                    aria-hidden="true"
                    alt="Hotels"
                    src={solutionsHotels}
                    style={{ verticalAlign: "bottom" }}
                  />
                </picture>
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                  Corporate
                </div>
              </div>
              {/* /First */}

              {/* Second */}
              <div className="tbk-relative tbk-grow tbk-basis-2/5 tbk-overflow-hidden lg:tbk-basis-auto">
                <picture className="js-progressive-image tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-lg ">
                  <source
                    srcSet={`${solutionsHospitality} 1x, ${solutionsHospitality2x} 2x`}
                    media="(minWidth: 760px)"
                    type="image/png"
                  />
                  <source
                    srcSet={`${solutionsHospitality} 1x, ${solutionsHospitality2x} 2x`}
                    type="image/png"
                  />
                  <img
                    className="tbk-inline-block tbk-h-full tbk-w-full tbk-rounded-lg tbk-object-cover"
                    aria-hidden="true"
                    alt="Hospitality"
                    src={solutionsHospitality}
                    style={{ verticalAlign: "bottom" }}
                  />
                </picture>
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                  Hospitality
                </div>
              </div>

              {/* /Second */}

              {/* Third */}
              <div className="tbk-relative tbk-basis-full tbk-overflow-hidden md:tbk-basis-auto">
                <div className={"tbk-pt-[44.692737430167598%]"}>
                  <picture className="js-progressive-image tbk-absolute tbk-top-0 tbk-left-0 tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-lg">
                    <source
                      srcSet={`${solutionsCoWorkingSpaces} 1x, ${solutionsCoWorkingSpaces2x} 2x`}
                      media="(minWidth: 760px)"
                      type="image/png"
                    />
                    <source
                      srcSet={`${solutionsCoWorkingSpaces} 1x, ${solutionsCoWorkingSpaces2x} 2x`}
                      type="image/png"
                    />
                    <img
                      className="tbk-inline-block tbk-h-full tbk-w-full tbk-object-cover"
                      aria-hidden="true"
                      alt="Co-Working Spaces"
                      src={solutionsCoWorkingSpaces}
                      style={{ verticalAlign: "bottom" }}
                    />
                  </picture>
                </div>
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-text-center tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                  <span className="tbk-whitespace-nowrap">Co-Working</span>{" "}
                  Spaces
                </div>
              </div>
              {/* /Third */}

              {/* Fourth */}
              <div className="tbk-relative tbk-grow tbk-basis-2/5 tbk-overflow-hidden lg:tbk-basis-auto">
                <picture className="js-progressive-image tbk-h-full tbk-w-full tbk-overflow-hidden tbk-rounded-lg ">
                  <source
                    srcSet={`${solutionsCareHomes} 1x, ${solutionsCareHomes2x} 2x`}
                    media="(minWidth: 760px)"
                    type="image/png"
                  />
                  <source
                    srcSet={`${solutionsCareHomes} 1x, ${solutionsCareHomes2x} 2x`}
                    type="image/png"
                  />
                  <img
                    className="tbk-inline-block tbk-h-full tbk-w-full tbk-rounded-lg tbk-object-cover"
                    aria-hidden="true"
                    alt="Care Homes"
                    src={solutionsCareHomes}
                    style={{ verticalAlign: "bottom" }}
                  />
                </picture>
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-top-1/2  tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-whitespace-nowrap tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                  Care Homes
                </div>
              </div>
              {/* /Fourth */}

              {/* Fifth */}
              <div className="tbk-relative tbk-grow tbk-basis-2/5 tbk-overflow-hidden lg:tbk-basis-auto">
                <picture className="js-progressive-image tbk-h-full tbk-w-full  tbk-overflow-hidden tbk-rounded-lg ">
                  <source
                    srcSet={`${solutionsEvents} 1x, ${solutionsEvents2x} 2x`}
                    media="(minWidth: 760px)"
                    type="image/png"
                  />
                  <source
                    srcSet={`${solutionsEvents} 1x, ${solutionsEvents2x} 2x`}
                    type="image/png"
                  />
                  <img
                    className="tbk-inline-block tbk-h-full tbk-w-full tbk-rounded-lg tbk-object-cover"
                    aria-hidden="true"
                    alt="Events"
                    src={solutionsEvents}
                    style={{ verticalAlign: "bottom" }}
                  />
                </picture>
                <div className="tbk-text-cta md:tbk-text-title-bold tbk-absolute tbk-top-1/2 tbk-left-1/2 tbk--translate-y-1/2 tbk--translate-x-1/2 tbk-uppercase tbk-text-basic-white md:tbk-normal-case">
                  Events
                </div>
              </div>

              {/* /Fifth */}
            </div>
          </div>

          <div className="tbk-flex tbk-hidden tbk-justify-center">
            <div className="tbk-w-126">
              <p className="tbk-text-main tbk-mb-0 tbk-text-center tbk-text-primary">
                TruBe specialises in delivering customised fitness and wellness
                solutions across diverse sectors. From energising events to
                promoting well-being in co-working spaces, enhancing corporate
                hospitality experiences, and providing tailored care for
                residents in care homes, TruBe offers versatile and expert
                services for a wide range of environments.
              </p>
            </div>
          </div>
        </div>
      </section>

      <AppBanner />

      <AskConciergeModal
        isOpen={isAskConciergeModalOpen}
        onClose={() => setIsAskConciergeModalOpen(false)}
      />

      {/*<RegionSelectModal
        isOpen={isRegionSelectModalOpen}
        onClose={() => setIsRegionSelectModalOpen(false)}
      />*/}

      <Footer />

      <Modal
        isOpen={isGalleryCarouselModalOpen}
        // onAfterOpen={this.afterOpenModal}
        onRequestClose={closeGalleryCarouselModal.bind(null)}
        style={modalStyles.galleryCarouselDefault}
        contentLabel="Gallery Carousel"
      >
        {/*<Link role='button' onClick={this.closeDateTimeSelectorModal}><Icon name='close' className='tb-pd-0' width='32px' height='32px' /></Link>*/}
        <div
          role="button"
          className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-p-1"
          onClick={closeGalleryCarouselModal}
        >
          <CloseIcon className="tbk-p-0 tbk-text-basic-white" />
        </div>
        <div className="modal-gallery-carousel u-d-flex_ u-flex-column_ tbk-h-full">
          <Carousel
            // images={images}
            hasIndexBoard={false}
            hasMediaButton={false}
            hasSizeButton={false}
            hasThumbnails={false}
            index={galleryInitialSlide}
            leftIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M8 1.5L6.5 0L0 6.5L6.5 13L8 11.5L3 6.5L8 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            rightIcon={
              <svg
                width="8"
                height="13"
                viewBox="0 0 8 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M0 1.5L1.5 0L8 6.5L1.5 13L0 11.5L5 6.5L0 1.5Z"
                  fill="#CBCFD5"
                />
              </svg>
            }
            style={{ height: "100%", width: "100%" }}
            // onIndexChange={({ curIndex, curIndexForDisplay }) => {
            //   const ytPlayerIframes = document.querySelectorAll(".yt_player_iframe");
            // }}
          >
            {galleryImages &&
              galleryImages.map((url) => (
                <figure className="_3iVQ0">
                  <img
                    className="_t1897 tbk-h-auto_ tbk-top-1/2 tbk-mx-auto tbk-w-auto tbk--translate-y-1/2 tbk-object-contain"
                    src={url}
                  />
                </figure>
              ))}
          </Carousel>
        </div>
      </Modal>

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          {/*<button*/}
          {/*  onClick={closeInfoModal}*/}
          {/*  className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"*/}
          {/*>*/}
          {/*  <CloseIcon />*/}
          {/*</button>*/}
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-1 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {infoModalTitle === "Referral Bonus Applied" ? (
            <CheckNiceIcon size={40} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <BlockerIcon size={40} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </>
  );
}
